import { UserCredential, auth, DocumentData } from "../config/firebase";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ContextProps = {
  currentUser: DocumentData | null;
  isReady: boolean;
  signInWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<UserCredential>;
  signOut: () => Promise<void>;
};

const SessionContext = createContext({} as ContextProps);

export default SessionContext;

type ProviderProps = {
  children: React.ReactNode;
};

export const SessionProvider = ({ children }: ProviderProps): JSX.Element => {
  const [currentUser, setCurrentUser] = useState<DocumentData | null>(null);
  const [isReady, setIsReady] = useState(false);

  const signInWithEmailAndPassword = (email: string, password: string) => {
    return auth.signInWithEmailAndPassword(email, password);
  };

  const signOut = useCallback(async () => {
    await auth.signOut();
  }, []);

  useEffect(
    () =>
      auth.onAuthStateChanged(async (user) => {
        if (user) {
          try {
            setCurrentUser(user);
          } catch (error) {
            console.log(`error`, error);
          }
        } else {
          setCurrentUser(null);
        }

        setIsReady(true);
      }),
    [signOut]
  );

  const value = useMemo(
    () => ({
      currentUser,
      isReady,
      signInWithEmailAndPassword,
      signOut,
    }),
    [currentUser, isReady, signOut]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

export const useSession = (): ContextProps => {
  return useContext(SessionContext);
};
